<script>
import Badge from '../UI/Badge.vue'
import SimpleGroupSelect from '../UI/Form/SimpleGroupSelect.vue'
import SimulationMenuItem from './SimulationMenuItem.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../Mixins/global'
import hasRole from '../../../../Utils/hasRole.ts'

export default {
  components: { SimulationMenuItem, SimpleGroupSelect, Badge },
  mixins: [mixinComponent, globalVariablesMixin],

  props: {
    active: { type: String, required: true },
    simulation: { type: Object, required: true },
    alertInformation: { type: Object, required: false },
  },

  computed: {
    isNotEligible: function () {
      return this.simulation.decision == null
    },
    isTender() {
      return this.simulation.finance.tender
    },
    menuClasses() {
      return {
        'topbar-submenu': true,
        status_check_UNKNOWN: this.simulation.status === 'CANCELED',
      }
    },
    numberClasses() {
      return {
        'col-md-4': !this.isTender,
        'col-md-3': this.isTender,
        'd-flex': true,
        'align-items-center': true,
      }
    },
    statusClasses() {
      return {
        col: true,
        'd-flex': true,
        'align-items-center': true,
        'justify-content-center': true,
      }
    },
    canSendDemandToRefinancer() {
      const clientInFormation = this.simulation?.client?.inFormation ?? true

      return clientInFormation === false
    },
    isActiveAlert() {
      return this.alertInformation.status !== 'ARCHIVED' && this.alertInformation.number > 0
    },
    alertNumberClasses() {
      return {
        'topbar-alert': true,
        [`topbar-alert-${this.alertInformation.blocking ? 'danger' : 'warning'}`]: true,
      }
    },
    alertClasses() {
      return {
        'topbar-contract-circle': true,
        'bell-container': this.isActiveAlert,
      }
    },
    availableMenuItems() {
      return this.menuItems.filter(({ show = true }) => show)
    },
    tabItems() {
      return this.availableMenuItems.filter(({ icon = null }) => icon === null)
    },
    actionItems() {
      return this.availableMenuItems.filter(({ icon = null }) => icon !== null)
    },
    menuItems() {
      return [
        {
          show: true,
          name: 'simulation.menu.quotation',
          link: 'devis',
          icon: null,
          disabled: false,
          active: this.active === 'devis',
        },
        {
          show:
            user.isBusinessMan ||
            user.isAdmin ||
            this.isTheme('financia', 'lbp', 'sequencefinance', 'hexapage') ||
            hasRole('ROLE_SALESMAN_EXT_PREMIUM_SHOW'),
          name: 'simulation.menu.service',
          link: 'service',
          icon: null,
          disabled: this.isNotEligible,
          active: this.active === 'service',
        },
        {
          show: true,
          name: 'simulation.menu.demand',
          link: 'demand',
          icon: null,
          disabled: this.isNotEligible,
          active: this.active === 'demand',
        },
        {
          show: !user.isReadOnly && this.hasRole('ROLE_DEMAND') && this.hasRole('ROLE_DECISION_VIEW_SHOW'),
          name: 'simulation.menu.decisions',
          link: 'decision',
          icon: null,
          disabled: this.canSendDemandToRefinancer === false,
          active: this.active === 'decision',
        },
        {
          show: this.hasRole('ROLE_RESTRUCTURING') && !!this.simulation.finance.restructuring,
          name: 'simulation.menu.restructuring',
          link: 'restructuring',
          icon: 'fa fa-refresh',
          disabled: false,
          active: this.active === 'restructuring',
        },
        {
          show: user.isBusinessMan && this.hasRole('ROLE_PROFITABILITY_SHOW'),
          name: 'simulation.menu.profitability',
          link: 'profitability',
          icon: 'fa fa-piggy-bank',
          disabled: this.isNotEligible,
          active: this.active === 'profitability',
        },
        {
          show: this.hasRole('ROLE_CONTRIBUTORS_SHOW'),
          name: 'simulation.menu.actors',
          link: 'actors',
          icon: 'fas fa-user',
          disabled: false,
          active: this.active === 'actors',
        },
        {
          show: this.hasRole('ROLE_COMMENTS_SHOW'),
          name: 'simulation.menu.comments',
          link: 'comments',
          icon: 'fas fa-comment',
          disabled: false,
          active: this.active === 'comments',
        },
        {
          show: this.hasRole('ROLE_ALERT_SHOW'),
          name: 'simulation.menu.alert',
          icon: 'fas fa-bell',
          link: 'alert',
          disabled: false,
          classes: {
            'bell-container': this.isActiveAlert,
          },
          badge: !this.isActiveAlert
            ? null
            : {
                class: this.alertNumberClasses,
                value: this.alertInformation?.number,
              },
        },
      ]
    },
  },

  methods: {
    handleNavigation(tab) {
      const tabEnabled = this[tab] ?? true
      if (!tabEnabled) {
        return
      }

      if (tab === 'alert') {
        this.showAlert()
        return
      }

      window.history.pushState(
        { p: tab },
        'Title',
        '/devis/simulation.php?i=' + this.simulation.id + '&p=' + tab + '&index=simulation',
      )
      this.selected = tab
      this.$emit('link', tab)
    },
    showAlert() {
      this.$emit('show-alert')
    },
  },
}
</script>
<template>
  <div :class="menuClasses">
    <div class="topbar-simulation-title simulation-menu">
      <div
        :class="numberClasses"
        v-html="$t('simulation.title.number', { number: simulation.id })"
        e2e-data="simulationNumber"
      ></div>
      <div
        :class="statusClasses"
        v-html="
          $t('simulation.title.statuses', {
            status: simulation.status ? $t('simulation.status.' + simulation.status + '.label') : '',
          })
        "
        e2e-data="simulationStatus"
      ></div>
      <div v-if="isTender" :class="statusClasses">
        <Badge type="warning">
          {{ $t('simulation.fields.finance.tender.label') }}
        </Badge>
      </div>
      <div class="col d-flex justify-content-end">
        <template v-for="item in actionItems" :key="item.name">
          <SimulationMenuItem
            v-if="item.icon !== null"
            :menu-item="item"
            @navigate="handleNavigation"
            :menu-name="item.name"
          >
            <template v-if="!!item.badge" #badge>
              <span :class="item.badge.class">{{ item.badge.value }}</span>
            </template>
          </SimulationMenuItem>
        </template>
      </div>
    </div>

    <hr class="my-1" style="background-color: white" />

    <div class="mobile-menu" v-if="availableMenuItems.length > 0">
      <SimpleGroupSelect
        ref-name="selecttabsimulation"
        id="selecttabsimulation"
        :pre-select-value="false"
        @transmitted="handleNavigation"
      >
        <div v-for="item in availableMenuItems">
          <option v-if="item.show" :key="item.name" :value="item.link">
            {{ $t(item.name) }}
          </option>
        </div>
      </SimpleGroupSelect>
    </div>

    <div class="device-menu d-flex justify-content-center" v-if="menuItems.length > 0">
      <div v-for="item in tabItems" :key="item.name">
        <SimulationMenuItem :key="item.name" :menu-item="item" @navigate="handleNavigation" :menu-name="item.name" />
      </div>
    </div>
  </div>
</template>
